import Text from "./Text";
import { useState, useEffect } from "react";
import TextContainer from "./TextContainer";
import { Col, Row } from "./Flexbox";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { IconButton } from "@mui/material";
import Button from "@mui/material/Button";
import { forgotPassword, confirmForgotPassword } from "../scripts/functions/AuthService";

const RestorePassword = ({theme, onClose, onSuccess, initialMail, tempCode, isDark}) => {
    const [step, setStep] = useState(tempCode ? 2 : 0);  //If we have the code, start on step 1
    const [email, setEmail] = useState(initialMail || "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");  

    const [formErrors, setFormErrors] = useState({});
    const [generalError, setGeneralError] = useState(null);

    const [validEmail, setValidEmail] = useState(false);
    const [validPassword, setValidPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const regex = /^[a-zA-Z]{3,}\S*@(?:\S*[a-zA-Z]){5,}\S*\.com(\.\S{2,3})?$/;

    const validateEmail = (mail) => {
        if(!mail.trim()) return "El correo es obligatorio";
        if (!regex.test(mail)) return "Formato de correo inválido";
        return null;
    }

    const validatePassword = (pass, confirm) => {
        const validations = [];
            if(password.length > 0) {
                if(password.length < 8) validations.push("La contraseña debe tener al menos 8 caracteres");

                if(!/[A-Z]/.test(password)) validations.push("La contraseña debe contener al menos una mayúscula");

                if(!/[0-9]/.test(password)) validations.push("La contraseña debe contener al menos un número");

                if (!/[!@#$%^&*()_+={}[\]|:;"'<>,.?/~]/.test(password)) validations.push("La contraseña debe contener al menos un carácter especial (!@#$%^&*...).");
            }

        const confirmErr = (confirm.length > 0 && password !== confirm) ? "Las contraseñas no coinciden" : null;
        const passErr = validations.length > 0 ? validations[0] : null;

        return {
            passErr, confirmErr, isValid: !passErr && !confirmErr && password.length > 0 && confirm.length > 0
        }
    }

    useEffect(() => {
        if (email === "") {
            setFormErrors(prev => ({ ...prev, email: null}));
            setValidEmail(false);
            return;
        }

        const timeout = setTimeout(() => {
            const error = validateEmail(email);
            setFormErrors(prev => ({ ...prev, email:error}));
            setValidEmail(!error);
            setGeneralError(null);
        }, 300);

        return () => clearTimeout(timeout);
    }, [email]);

    useEffect(() => {
        if (password === "" && confirmPassword === "") {
            setFormErrors(prev => ({ ...prev, password: null, confirmPassword: null}));
            setValidPassword(false);
            return;
        }

        const timeout = setTimeout(() => {
            const {passErr, confirmErr, isValid} = validatePassword(password, confirmPassword);
            setFormErrors(prev => ({ ...prev, password: passErr, confirmPassword: confirmErr}));
            setValidPassword(isValid);
            setGeneralError(null);
        }, 300);

        return () => clearTimeout(timeout);
    }, [confirmPassword, password]);

    const handleSendCode = async (e) => {
        e.preventDefault();
        setGeneralError(null);

        const emailError = validateEmail(email);
        if(emailError) {
            setFormErrors(prev => ({...prev, email: emailError}));
        }
        setIsProcessing(true);

        try {
            await forgotPassword(email);
            setStep(1);
            setFormErrors({}); //Clean the errors
        } catch(e) {
            setGeneralError("Hubo un error al enviar el correo de recuperación");
            console.error("Error al enviar el correo de recuperación: ", e);
        } finally {
            setIsProcessing(false);
        }
    }

    const handleConfirmation = async (e) => {
        e.preventDefault();
        setGeneralError(null);

        const {passErr, confirmErr, isValid} = validatePassword(password, confirmPassword);
        if (!isValid) {
            setFormErrors(prev => ({...prev, password: passErr, confirmPassword: confirmErr}));
            return;
        }
        
        setIsProcessing(true);
        try {
            await confirmForgotPassword(tempCode, password);
            onSuccess(password);
        } catch(e) {
            setGeneralError("Hubo un error al actualizar la contraseña");
            console.error("Hubo un error al actualizar la contraseña: ", e);
        } finally {
            setIsProcessing(false);
        }
    }

    return (
        <form onSubmit={step === 0 ? handleSendCode : handleConfirmation}>
            <div style={{position: 'fixed', top:0, left:0, right:0, bottom:0, background: 'rgba(0, 0, 0, 0.5)', alignItems: 'center', display: 'flex', justifyContent: 'center', zIndex: 1000, boxSizing: 'border-box'}}>
                <Col style={{width:'100%', maxWidth: '600px', maxHeight: '450px', flex:'none', alignItems:'flex-start', paddingLeft: 32, paddingRight: 32, paddingTop: 32, paddingBottom: 32, marginTop: 32, backgroundColor: theme.White,  borderRadius: 4, boxShadow: isDark ? '0px 10px 40px rgba(0, 0, 0, 0.8)' : '0px 10px 30px rgba(0, 0, 0, 0.1)'}}>
                    <Col style={{height: 65, flex:'none', width: '100%', alignItems:'flex-start', justifyContent: 'space-between'}}>
                        <Text color={theme.Black} size={21} weight={"bold"}>Actualizar contraseña</Text>
                    </Col>
                    {step === 0 && (
                        <Col style={{alignItems: 'flex-start', justifyContent: 'space-between', width: '100%'}}>
                            <Col style={{alignItems: 'start', width: '100%', gap: 8}}>
                                <Text color={theme.Black} size={15} weight={"medium"}>Correo electrónico</Text>
                                <TextContainer autoComplete="username" style={{width: '100%', height: 36}} value={email} theme={theme} placeHolder="something@domain.com.mx" onChange={(value) => setEmail(value)} />
                                {generalError && (
                                    <Text style={{paddingTop: 5}} color={theme.Red} size={15} weight={"medium"}>{generalError}</Text>
                                )}
                            </Col>
                            <Row style={{width: '100%', justifyContent: 'end', paddingTop: 64, gap: 16}}>
                                <Row style={{flex: 1, justifyContent: 'end', gap: 32}}>
                                    <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={theme.Lao} size={15} onClick={onClose}>Cancelar</Text>
                                    <Button disableElevation type="submit" disabled={!validEmail || isProcessing} variant="text" sx={{height: 20, backgroundColor: 'transparent', color: !validEmail ? theme.Ey : theme.Magenta, fontSize: 15, textTransform: 'none', '&.Mui-disabled': {color: isDark ? theme.Audi : theme.Ey}}}>Solicitar correo de recuperación</Button>            
                                </Row>
                            </Row>
                        </Col>
                    )}
                    {step === 1 && (
                        <Col style={{alignItems: 'start', width: '100%', gap: 8}}>
                            <Text color={theme.Black} size={17} weight={"medium"}>Se han enviado instrucciones a tu correo electrónico para actualizar tu contraseña, revisa tu bandeja de entrada y SPAM.</Text>
                            <Row style={{flex: 1, justifyContent: 'end', gap: 32, paddingTop: 32}}>
                                <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={theme.Lao} size={15} onClick={onClose}>Cerrar</Text>
                            </Row>
                        </Col>
                    )}
                    {step === 2 && (
                        <Col style={{alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', gap: 32}}>
                            <Col style={{alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', gap: 40}}>
                                <Col style={{alignItems: 'start', width: '100%', gap: 8, position: 'relative'}}>
                                    <Text color={theme.Black} size={15} weight={"medium"}>Nueva contraseña</Text>
                                    <TextContainer type={showPassword ? "text" : "password"} style={{width: '100%', height: 36}} value={password} theme={theme} placeHolder="" onChange={(value) => setPassword(value)} endIcon={<IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" style={{color: theme.Lao}}>{showPassword ? <VisibilityOff/> : <Visibility/>}</IconButton>}/>
                                    {formErrors.password && (
                                        <Text style={{top: 67, position: 'absolute'}} color={theme.Red} size={15} weight={"medium"}>{formErrors.password}</Text>
                                    )}
                                </Col>
                                <Col style={{alignItems: 'start', width: '100%', gap: 8, position: 'relative'}}>
                                    <Text color={theme.Black} size={15} weight={"medium"}>Confirmar nueva contraseña</Text>
                                    <TextContainer type={showPassword ? "text" : "password"} style={{width: '100%', height: 36}} value={confirmPassword} theme={theme} placeHolder="" onChange={(value) => setConfirmPassword(value)} endIcon={<IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end" style={{color: theme.Lao}}>{showPassword ? <VisibilityOff/> : <Visibility/>}</IconButton>}/>
                                    {formErrors.confirmPassword && (
                                        <Text style={{top: 67, position: 'absolute'}} color={theme.Red} size={15} weight={"medium"}>{formErrors.confirmPassword}</Text>
                                    )}
                                </Col>
                            </Col>
                            <Row style={{width: '100%', justifyContent: 'end', gap: 16}}>
                                <Row style={{flex: 1, justifyContent: 'end', gap: 32}}>
                                    <Text style={{ cursor:'pointer', transition:'transform 2s ease'}} color={theme.Lao} size={15} onClick={onClose}>Cancelar</Text>
                                    <Button disableElevation type="submit" disabled={!validPassword || isProcessing} variant="text" sx={{height: 20, backgroundColor: 'transparent', color: !validPassword ? theme.Ey : theme.Magenta, fontSize: 15, textTransform: 'none', '&.Mui-disabled': {color: isDark ? theme.Audi : theme.Ey}}}>Continuar</Button>            
                                </Row>
                            </Row>
                        </Col>
                    )}
                </Col>
            </div>
        </form>
    )
}

export default RestorePassword;